import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { LoadingSpinner, ErrorBanner } from '../components/AdminStatus';

const API  = 'https://api.udanchu.com';
const font = "'DM Sans', system-ui, sans-serif";
const mono = "'DM Mono', monospace";

const getKey = () => sessionStorage.getItem('udanchu_admin_key') || '';

const toLines  = arr  => (arr || []).join('\n');
const fromLines = str => str.split('\n').map(l => l.trim()).filter(Boolean);

const s = {
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  topLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  backBtn: {
    background: 'transparent',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#64748b',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '500',
    padding: '7px 14px',
    cursor: 'pointer',
  },
  cardId: {
    color: '#64748b',
    fontFamily: mono,
    fontSize: '13px',
  },
  saveBtn: {
    background: '#4f8ef7',
    border: 'none',
    borderRadius: '7px',
    color: '#fff',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '600',
    padding: '7px 20px',
    cursor: 'pointer',
  },
  saveBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  row2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
  },
  label: {
    color: '#64748b',
    fontFamily: font,
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  input: {
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '14px',
    padding: '10px 13px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '14px',
    padding: '10px 13px',
    outline: 'none',
    width: '100%',
    cursor: 'pointer',
  },
  textarea: {
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '14px',
    lineHeight: '1.6',
    padding: '10px 13px',
    outline: 'none',
    resize: 'vertical',
    width: '100%',
    boxSizing: 'border-box',
  },
  monoTextarea: {
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#94a3b8',
    fontFamily: mono,
    fontSize: '12px',
    lineHeight: '1.6',
    padding: '10px 13px',
    outline: 'none',
    resize: 'vertical',
    width: '100%',
    boxSizing: 'border-box',
  },
  hint: {
    color: '#475569',
    fontFamily: font,
    fontSize: '11px',
  },
  successBox: {
    background: 'rgba(52,211,153,0.1)',
    border: '1px solid rgba(52,211,153,0.3)',
    borderRadius: '8px',
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: font,
  },
  successDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#34d399',
    flexShrink: 0,
  },
  successText: {
    color: '#34d399',
    fontSize: '14px',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #252a38',
    margin: '4px 0',
  },
};

function Field({ label, hint, children }) {
  return (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      {children}
      {hint && <span style={s.hint}>{hint}</span>}
    </div>
  );
}

export default function AdminCardEdit() {
  const { id }     = useParams();
  const navigate   = useNavigate();

  const [loading,  setLoading]  = useState(true);
  const [fetchErr, setFetchErr] = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [saveErr,  setSaveErr]  = useState(null);
  const [success,  setSuccess]  = useState(false);
  const [attempt,  setAttempt]  = useState(0);

  // Form fields
  const [title,         setTitle]         = useState('');
  const [domain,        setDomain]        = useState('traffic');
  const [status,        setStatus]        = useState('active');
  const [shortAnswer,   setShortAnswer]   = useState('');
  const [explanation,   setExplanation]   = useState('');
  const [lawRefsRaw,    setLawRefsRaw]    = useState('[]');
  const [triggerLines,  setTriggerLines]  = useState('');
  const [actionLines,   setActionLines]   = useState('');
  const [edgeLines,     setEdgeLines]     = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchErr(null);

    fetch(`${API}/cards/${id}`, { headers: { 'X-Admin-Key': getKey() } })
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(j.message || `Error ${r.status}`)))
      .then(card => {
        if (cancelled) return;
        setTitle(card.title || '');
        setDomain(card.domain || 'traffic');
        setStatus(card.status || 'active');
        setShortAnswer(card.short_answer || '');
        setExplanation(card.explanation || '');
        setLawRefsRaw(JSON.stringify(card.law_references || [], null, 2));
        setTriggerLines(toLines(card.trigger_queries));
        setActionLines(toLines(card.action_steps));
        setEdgeLines(toLines(card.edge_cases));
      })
      .catch(err => { if (!cancelled) setFetchErr(typeof err === 'string' ? err : 'Failed to load card.'); })
      .finally(()  => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [id, attempt]);

  const handleSave = async () => {
    setSaving(true);
    setSaveErr(null);
    setSuccess(false);

    let parsedLawRefs;
    try {
      parsedLawRefs = JSON.parse(lawRefsRaw);
    } catch {
      setSaveErr('Law References is not valid JSON. Fix it before saving.');
      setSaving(false);
      return;
    }

    const body = {
      title:           title.trim(),
      domain,
      status,
      short_answer:    shortAnswer.trim(),
      explanation:     explanation.trim(),
      law_references:  parsedLawRefs,
      trigger_queries: fromLines(triggerLines),
      action_steps:    fromLines(actionLines),
      edge_cases:      fromLines(edgeLines),
    };

    try {
      const res = await fetch(`${API}/cards/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key':  getKey(),
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSuccess(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const j = await res.json().catch(() => ({}));
        setSaveErr(j.message || j.error || `Save failed (${res.status})`);
      }
    } catch {
      setSaveErr('Could not reach the API. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Edit Card">
      <div style={s.topBar}>
        <div style={s.topLeft}>
          <button style={s.backBtn} onClick={() => navigate(`/admin/card/${id}`)}>← Back</button>
          <span style={s.cardId}>{id}</span>
        </div>
        <button
          style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {loading && <LoadingSpinner message="Loading card..." />}

      {!loading && fetchErr && (
        <ErrorBanner error={fetchErr} onRetry={() => setAttempt(a => a + 1)} />
      )}

      {!loading && !fetchErr && (
        <div style={s.form}>
          {success && (
            <div style={s.successBox}>
              <div style={s.successDot} />
              <span style={s.successText}>Card saved successfully.</span>
            </div>
          )}

          {saveErr && <ErrorBanner error={saveErr} onRetry={() => setSaveErr(null)} />}

          <div style={s.row2}>
            <Field label="Domain">
              <select style={s.select} value={domain} onChange={e => setDomain(e.target.value)}>
                <option value="traffic">traffic</option>
                <option value="consumer">consumer</option>
                <option value="police">police</option>
              </select>
            </Field>
            <Field label="Status">
              <select style={s.select} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="deprecated">deprecated</option>
              </select>
            </Field>
          </div>

          <Field label="Title">
            <input
              style={s.input}
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </Field>

          <hr style={s.divider} />

          <Field label="Short Answer">
            <textarea
              style={{ ...s.textarea, minHeight: '80px' }}
              value={shortAnswer}
              onChange={e => setShortAnswer(e.target.value)}
            />
          </Field>

          <Field label="Explanation">
            <textarea
              style={{ ...s.textarea, minHeight: '120px' }}
              value={explanation}
              onChange={e => setExplanation(e.target.value)}
            />
          </Field>

          <hr style={s.divider} />

          <Field label="Law References" hint="Edit as raw JSON array">
            <textarea
              style={{ ...s.monoTextarea, minHeight: '160px' }}
              value={lawRefsRaw}
              onChange={e => setLawRefsRaw(e.target.value)}
            />
          </Field>

          <hr style={s.divider} />

          <Field label="Trigger Queries" hint="One per line">
            <textarea
              style={{ ...s.textarea, minHeight: '100px' }}
              value={triggerLines}
              onChange={e => setTriggerLines(e.target.value)}
            />
          </Field>

          <Field label="Action Steps" hint="One per line">
            <textarea
              style={{ ...s.textarea, minHeight: '100px' }}
              value={actionLines}
              onChange={e => setActionLines(e.target.value)}
            />
          </Field>

          <Field label="Edge Cases" hint="One per line">
            <textarea
              style={{ ...s.textarea, minHeight: '80px' }}
              value={edgeLines}
              onChange={e => setEdgeLines(e.target.value)}
            />
          </Field>
        </div>
      )}
    </AdminLayout>
  );
}
